"use client";

import { useEffect } from "react";
import { usePlan } from "@/lib/platform/usePlan";
import UpgradeToPro from "@/components/platform/UpgradeToPro";
import { trackEvent } from "@/lib/analytics/track";

// Generic "soft paywall" wrapper — renders children normally for Pro
// subscribers, otherwise shows a blurred preview + upgrade prompt. Content
// still renders server-side (good for SEO, same pattern as e.g. Medium's
// paywall) — the gating is purely a client-side viewing restriction.
export default function ProGate({ label, children }: { label: string; children: React.ReactNode }) {
  const { isPro, loading } = usePlan();

  // Currently only used to gate Places visitor voice notes — tagged with a
  // "source" so the admin funnel can tell this paywall impression apart
  // from the generic one UpgradeToPro fires on its own (e.g. from /pricing).
  useEffect(() => {
    if (!loading && !isPro) trackEvent("payment_page_open", { flow: "pro_subscription", source: "places_voice_notes" });
  }, [loading, isPro]);

  if (loading || isPro) return <>{children}</>;

  return (
    <div className="relative overflow-hidden rounded-2xl">
      <div className="pointer-events-none select-none blur-sm">{children}</div>
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-white/70 p-6 text-center dark:bg-[#0b1220]/80">
        <p className="text-sm font-semibold text-slate-900 dark:text-white">{label}</p>
        <div className="w-full max-w-xs">
          <UpgradeToPro />
        </div>
      </div>
    </div>
  );
}
